<script setup>
import Layout from "../Layouts/Customer.vue";
import Checkbox from "primevue/checkbox";
import InputText from "primevue/inputtext";
import {VueSignaturePad} from "vue-signature-pad";
import Button from "primevue/button";
import {computed, ref} from "vue";
import {useToast} from "primevue/usetoast";
import moment from "moment";
import {useForm} from "@inertiajs/inertia-vue3";
import {true$} from "../utils/validators";
import {useVuelidate} from "@vuelidate/core";
import {scrollToFirstError} from "../utils/form";
import {dataURLtoFile} from "../utils/file-utils";
import ConsentSignature from "../Components/ConsentSignature.vue";
import {Inertia} from "@inertiajs/inertia";


const props = defineProps([
    'customer',
    'menuItems',
    'flash',
]);
const toast = useToast()
const consent = ref(props.customer?.icoone_etc_consent)
const consentSlug = ref('icoone-etc-consents')
const isEdit = ref(Boolean(consent.value))
const submitting = ref(false)
const consentSignature = ref(null)
const date = ref(
    isEdit.value ? moment(consent.value.updated_at) : moment()
)
const form = useForm({
    signature_key: null,
    customer_uuid: props.customer.uuid,
    is_agree_signature: isEdit.value,
    signature_img: isEdit.value ? consent.value.signature_img : null,
    has_signature: null,
    date: date.value.format('DD.MM.YYYY'),
});

const rules = {
    form: {
        is_agree_signature: {
            true$,
        },
        signature_key: {
            custom$: {
                $validator: function(value) {
                    return form.has_signature !== false
                },
                $message: 'Dieses Feld ist erforderlich',
            }
        }
    }
}

const v$ = useVuelidate(rules, {
    form,
})

const submit = async (e, isDownload) => {
    let signatureData = null
    const signaturePad = consentSignature.value.getSignaturePad()

    if (signaturePad) {
        const { isEmpty, data } = signaturePad.saveSignature()

        form.has_signature = ! isEmpty
        signatureData = data
    }

    v$.value.$validate()

    if (v$.value.$errors.length) {
        scrollToFirstError()
        toast.add({
            severity: "error",
            summary: "Error",
            life: 5000,
        });

        return
    }

    submitting.value = true

    if (signatureData) {
        const { key:signatureKey } = await Vapor.store(dataURLtoFile(signatureData), {
            progress: progress => {
            }
        })

        form.signature_key = signatureKey
    }

    const postUrl = isEdit.value
        ? `/${consentSlug.value}/${consent.value.uuid}`
        : `/${consentSlug.value}`

    form.post(
        postUrl,
        {
            preserveState: false,
            onError: (errors) => {
                toast.add({
                    severity: "error",
                    summary: "Error",
                    life: 5000,
                });
            },

            onSuccess: (page) => {
                toast.add({
                    severity: "success",
                    summary: "Aktualisiert",
                    life: 5000,
                });

                if (isDownload) {
                    window.location.href = `/${consentSlug.value}/${consent.value.uuid}/download`
                }
            },

            onFinish: (visit) => {
                submitting.value = false;
            }
        }
    )
}

const download = async () => {
    if (! submitting.value) {
        await submit(null, true)
    }
}
</script>
<template>
    <Layout :customer="customer" :menu-items="menuItems" :flash="flash">
        <div class="px-14 py-7">
            <div class="relative mb-9">
                <h1 class="text-black text-base font-bold mb-9">
                    Einverständniserklärung und Vertrag für Behandlung mit
                    Icoone Laser Med, Emsculpt und Inshape, Cellgym, Pneumatron,
                    Medical CAT, Avantgarde Medical
                </h1>

                <div v-if="isEdit"
                     class="absolute top-0 right-0">
                    <i class="pi pi-file-pdf text-[#C5C6CC] cursor-pointer" @click="download"></i>
                </div>
            </div>

            <div class="text-sm">
                <h3 class="text-black font-bold">Einführung</h3>

                <div class="flex flex-col gap-3 mb-9">
                    <div class="flex flex-col gap-2">
                        <h4>Icoone Laser Med</h4>

                        <p>Die Behandlung mit dem Icoone Laser Med ist ein nicht- invasives Verfahren zur Hautstraffung,
                            Lymphdrainage und zur Reduktion von Fettdepots. Das Gerät kombiniert mechanische Stimulation mit
                            Laserenergie, um das Hautbild zu verbessern und Körperkonturen zu straffen.
                        </p>
                    </div>

                    <div class="flex flex-col gap-2">
                        <h4>Emsculpt und Inshape</h4>

                        <p>Emsculpt ist ein nicht-invasives Verfahren zur Muskelstraffung und Fettverbrennung. Es verwendet
                            hochintensive, fokussierte, elektromagnetische (HIFEM) Technologie, um Muskelkontraktionen zu
                            erwirken, die stärker sind als willkürliche Kontraktionen.
                        </p>
                    </div>

                    <div class="flex flex-col gap-2">
                        <h4>Cellgym</h4>

                        <p>Das Hypoxie-Hyperoxie-Gerät der Firma Cellgym wird verwendet, um die Leistungsfähigkeit und
                            Regeneration des Körpers zu verbessern. Durch kontrollierte Wechsel zwischen hypoxischen
                            (Sauerstoffmangel) und hyperoxischen (Sauerstoffüberschuss) Phasen wird der Körper trainiert, seine
                            Anpassungs- und Regenerationsfähigkeit zu steigern.
                        </p>
                    </div>

                    <div class="flex flex-col gap-2">
                        <h4>Pneumatron</h4>

                        <p>
                            Der Pneumatron200 ist ein Gerät zur pneumatischen Pulsationsmassage. Es wird verwendet, um die
                            Durchblutung zu fördern, Verspannungen zu lösen und den Lymphfluss zu verbessern. Das Gerät
                            arbeitet durch pulsierende Druckwellen, die auf das Gewebe einwirken.
                        </p>
                    </div>

                    <div class="flex flex-col gap-2">
                        <h4>
                            Medical CAT
                        </h4>

                        <p>
                            Das CO2-Therapiegerät CAT der Firma Cellgym verwendet ein Kohlendioxid-Trockenbad, um die
                            Durchblutung zu verbessern und den Zellstoffwechsel zu fördern. Der Patient liegt in einer
                            Hülle, die mit CO2-Gas gefüllt wird, was zu einer verbesserten Sauerstoffversorgung des Gewebes führt.
                        </p>
                    </div>

                    <div class="flex flex-col gap-2">
                        <h4>
                            Avantgarde Medical
                        </h4>

                        <p>
                            Die intermittierende Vakuumtherapie (IVT) der Firma Weyergans ist eine Behandlungsmethode, die
                            mittels Unterdruck (Vakuum) und Normaldruck (Kompression) die Durchblutung und den Lymphfluss
                            verbessert. Der Patient liegt in einer speziellen Kammer, in der abwechselnd Vakuum und Kompression
                            angewendet wird, was die Mikrozirkulation und den Stoffwechsel in den behandelten Körperregionen
                            fördert.
                        </p>
                    </div>
                </div>

                <ul class="mb-9">
                    <li>
                        <h3 class="text-sm font-bold my-5">
                            1. Vertragsgegenstand
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Der Kunde beauftragt VIGORY® mit der Durchführung von Behandlungen mit den oben aufgerührten
                                Technologien.
                            </p>

                            <p>
                                Umfang und Preise der Leistungen sind auf der VIGORY-Website ersichtlich und können unter dem
                                folgenden Link abgerufen werden [Einfügen Link/Verweis]. Es gelten die im Zeitpunkt der Buchung
                                ausgewiesenen Preise.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            2. Behandlungsablauf
                        </h3>

                        <div class="flex flex-col gap-3">
                            <div class="flex flex-col gap-2">
                                <h4>
                                    Icoone Laser Med
                                </h4>

                                <div class="flex flex-col gap-1">
                                    <p>
                                        Das Icoone Laser Med Gerät wird über die Haut geführt, um die gewünschten Bereiche zu behandeln.
                                    </p>

                                    <p>
                                        Die Behandlung dauert in der Regel zwischen 30 und 60 Minuten, je nach Größe des behandelten
                                        Bereichs und den spezifischen Zielen des Patienten.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col gap-2">
                                <h4>
                                    Emsculpt und Inshape
                                </h4>

                                <div class="flex flex-col gap-1">
                                    <p>
                                        Es werden insgesamt 6-8 Behandlungen pro Areal empfohlen
                                    </p>

                                    <p>
                                        Für die Behandlung sollte bequeme Kleidung getragen werden ohne metallische Komponenten wie
                                        Reissverschlüsse oder Knöpfe. Es darf kein Schmuck getragen werden und die Nutzung von
                                        elektronischen Geräten während der Behandlung ist nicht gestattet.
                                    </p>

                                    <p>
                                        Vor der Behandlung wird der zu behandelnde Bereich markiert.
                                    </p>

                                    <p>
                                        Das Emsculpt Gerät wird auf den Zielbereich aufgesetzt und aktiviert.
                                    </p>

                                    <p>
                                        Eine Sitzung dauert in der Regel etwa 30 Minuten, abhängig vom behandelten Bereich.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col gap-2">
                                <h4>
                                    Cellgym
                                </h4>

                                <div class="flex flex-col gap-1">
                                    <p>
                                        Vor der Behandlung wird eine Einweisung in die Nutzung des Geräts durchgeführt.
                                    </p>

                                    <p>
                                        Der Patient atmet über eine Maske, die abwechselnd hypoxische und hyperoxische Luft liefert. Eine
                                        Sitzung dauert in der Regel zwischen 30 und 60 Minuten, abhängig vom individuellen Behandlungsplan.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col gap-2">
                                <h4>
                                    Pneumatron
                                </h4>

                                <div class="flex flex-col gap-1">
                                    <p>
                                        Vor der Behandlung wird der zu behandelnde Bereich freigelegt.
                                    </p>

                                    <p>
                                        Das Pneumatron200 Gerät wird auf die Haut aufgesetzt und aktiviert, wobei pulsierende Druckwellen in
                                        das Gewebe gesendet werden.
                                    </p>

                                    <p>
                                        Eine Sitzung dauert in der Regel zwischen 20 und 45 Minuten, abhängig vom behandelten Bereich und
                                        den individuellen Bedürfnissen des Patienten.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col gap-2">
                                <h4>
                                    Medical CAT
                                </h4>

                                <div class="flex flex-col gap-1">
                                    <p>
                                        Vor der Behandlung wird der Patient über den Ablauf informiert und vorbereitet.
                                    </p>

                                    <p>
                                        Der Patient legt sich in eine spezielle Kammer oder einen Beutel, der dann mit CO2-Gas gefüllt
                                        wird.
                                    </p>

                                    <p>
                                        Es werden insgesamt 10-15 Behandlungen empfohlen.
                                    </p>

                                    <p>
                                        Die Sitzung dauert in der Regel zwischen 25 und 35 Minuten, abhängig vom individuellen
                                        Behandlungsplan.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col gap-2">
                                <h4>
                                    Avantgarde Medical
                                </h4>

                                <div class="flex flex-col gap-1">
                                    <p>
                                        Vor der Behandlung wird der Patient über den Ablauf informiert und vorbereitet.
                                    </p>

                                    <p>
                                        Der Patient legt sich in eine spezielle Kammer, die die Beine und/oder den Unterkörper umschließt. Es
                                    </p>

                                    <p>
                                        wird ein wechselnder Druck (Unterdruck und Normaldruck) angewendet, der in bestimmten Intervallen
                                        erfolgt.
                                    </p>

                                    <p>
                                        Eine Sitzung dauert in der Regel zwischen 30 und 45 Minuten.
                                    </p>

                                    <p>
                                        Es werden insgesamt 10-15 Behandlungen empfohlen, abhängig vom individuellen Behandlungsplan und
                                        den gewünschten Ergebnissen.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </li>



                    <li>
                        <h3 class="text-sm font-bold my-5">
                            3. Risiken und Nebenwirkungen
                        </h3>

                        <div class="flex flex-col gap-3">
                            <div class="flex flex-col gap-2">
                                <h4>
                                    Icoone Laser Med
                                </h4>

                                <div class="flex flex-col gap-1">
                                    <p>
                                        Wie bei jeder kosmetischen Behandlung gibt es potenzielle Risiken und Nebenwirkungen, die auftreten
                                        können. Dazu gehören, sind aber nicht beschränkt auf:
                                    </p>

                                    <p>
                                        Rötung oder leichte Schwellung im behandelten Bereich, vorübergehende Hautempfindlichkeit oder
                                        leichte Schmerzen, blaue Flecken oder Hämatome, insbesondere bei empfindlicher Haut
                                        Seltene Fälle von Hautreizungen oder allergischen Reaktionen.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col gap-2">
                                <h4>
                                    Emsculpt und Inshape
                                </h4>

                                <div class="flex flex-col gap-1">
                                    <p>
                                        Wie bei jeder Behandlung gibt es potenzielle Risiken und Nebenwirkungen, die auftreten können. Dazu
                                        gehören, sind aber nicht beschränkt auf:
                                    </p>

                                    <p>
                                        Muskelkater oder -schmerzen im behandelten Bereich, leichte Rötung oder Schwellung, vorübergehendes
                                        Taubheitsgefühl oder Kribbeln.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col gap-2">
                                <h4>
                                    Cellgym
                                </h4>

                                <div class="flex flex-col gap-1">
                                    <p>
                                        Wie bei jeder Behandlung gibt es potenzielle Risiken und Nebenwirkungen, die auftreten können. Dazu
                                        gehören, sind aber nicht beschränkt auf:
                                    </p>

                                    <p>
                                        Vorübergehender Schwindel oder Benommenheit, Kopfschmerzen, leichte Übelkeit, vorübergehende
                                        Müdigkeit oder Erschöpfung.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col gap-2">
                                <h4>
                                    Pneumatron
                                </h4>

                                <div class="flex flex-col gap-1">
                                    <p>
                                        Wie bei jeder Massage- oder Druckbehandlung gibt es potenzielle Risiken und Nebenwirkungen, die
                                        auftreten können. Dazu gehören, sind aber nicht beschränkt auf:
                                    </p>

                                    <p>
                                        Leichte Rötung oder Schwellung im behandelten Bereich, vorübergehend verstärkte Muskelschmerzen,
                                        Unbehagen oder Druckgefühl. Seltene Fälle von Hämatomen oder Blutergüssen, insbesondere bei
                                        empfindlicher Haut. Vorübergehende Müdigkeit nach der Behandlung.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col gap-2">
                                <h4>
                                    Medical CAT
                                </h4>

                                <div class="flex flex-col gap-1">
                                    <p>
                                        Wie bei jeder Behandlung gibt es potenzielle Risiken und Nebenwirkungen, die auftreten können. Dazu
                                        gehören, sind aber nicht beschränkt auf:
                                    </p>

                                    <p>
                                        Leichte Rötung oder Wärmegefühl auf der Haut, vorübergehende Atembeschwerden oder ein Gefühl der
                                        Enge in der Brust , Schwindel oder Benommenheit, leichte Übelkeit.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col gap-2">
                                <h4>
                                    Avantgarde Medical
                                </h4>

                                <div class="flex flex-col gap-1">
                                    <p>
                                        Wie bei jeder Behandlung gibt es potenzielle Risiken und Nebenwirkungen, die auftreten können. Dazu
                                        gehören, sind aber nicht beschränkt auf:
                                    </p>

                                    <p>
                                        Leichte Hautrötungen oder ein Wärmegefühl an den behandelten Stellen, vorübergehende Schwellungen
                                        oder Blutergüsse, Schwindel oder Benommenheit, Leichte Muskelkater oder Krämpfe.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            4. Vorbehalte und Gesundheitsschutz
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                VIGORY behält sich das Recht vor, die Behandlung abzulehnen oder abzubrechen, insbesondere wenn
                                gesundheitliche Bedenken vorliegen.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            5. Leistungsinhalt und Haftung
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Es kann bis zu 3 Monate dauern, bis die endgültigen Ergebnisse vorliegen. In seltenen Fällen kann der
                                Erfolg gering oder ausbleibend sein.
                            </p>

                            <p>
                                Der Leistungsinhalt umfasst die fachgerechte Durchführung der Behandlung.
                            </p>

                            <p>
                                VIGORY garantiert keinen spezifischen Behandlungserfolg.
                            </p>

                            <p>
                                VIGORY haftet gemäss den gesetzlichen Vorschriften für Verletzungen von vertraglichen und
                                ausservertraglichen Pflichten. Schadenersatz wird bei Vorsatz und grober Fahrlässigkeit geleistet. Bei
                                leichter Fahrlässigkeit haftet VIGORY nur bei Verletzung von Leben, Körper oder Gesundheit sowie bei
                                Verletzung wesentlicher Vertragspflichten. In diesen Fällen ist die Haftung auf den vorhersehbaren,
                                typischerweise eintretenden Schaden begrenzt. VIGORY haftet nicht für Schäden, die auf Umstände
                                zurückzuführen sind, für die der Kunde verantwortlich ist.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            6. Terminverschiebungen und -absagen
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Vereinbarte Folgetermine sind verbindlich. VIGORY stellt Personal und technische Ausstattung für die
                                Termine bereit. Terminverschiebungen sind kostenfrei, wenn sie mindestens 24 Stunden vor dem
                                vereinbarten Termin erfolgen.
                            </p>

                            <p>
                                Bei Absagen innerhalb von 24 Stunden vor dem Termin oder bei Nichterscheinen kann VIGORY die
                                vereinbarte Vergütung verlangen. Einsparungen oder anderweitige Verwendung der Kapazitäten werden
                                berücksichtigt.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            7. Datenschutz
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                VIGORY erhebt und bearbeitet Personendaten des Kunden nach Massgabe der Datenschutzerklärung, die
                                unter folgendem Link abgerufen werden kann: https://www.vigory.ch/datenschutz
                            </p>

                            <p>
                                Der Kunde stimmt der fotografischen Dokumentation der behandelten Bereiche und der anonymen
                                Veröffentlichung der Ergebnisse zu Werbezwecken zu.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            8. Zustimmung zur Behandlung
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Ich bestätige hiermit, dass ich die oben genannten Informationen zu den verschiedenen Therapien
                                gelesen und verstanden habe. Mir ist bewusst, dass jeder Mensch individuell reagiert und gewisse
                                Nebeneffekte auftreten können und dass das Behandlungsergebnis negativ beeinträchtigt werden
                                kann, wenn das definierte Therapiekonzept nicht zu Ende geführt wird. Ich kenne die Risiken und
                                möglichen Nebenwirkungen und willige freiwillig und nach ausreichender Überlegung in die
                                Durchführung der Behandlungen ein. Ich hatte die Gelegenheit, Fragen zu stellen, und diese wurden zu
                                meiner Zufriedenheit beantwortet.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            9. Haftungsausschluss
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Ich verstehe, dass das Ergebnis der Behandlung variieren kann und keine spezifischen Resultate
                                garantiert werden können. Ich entbinde hiermit die behandelnde Fachkraft, sowie das Center von
                                jeglicher Haftung, soweit gesetzlich zulässig.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            10. Schlussbestimmungen
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Die Angaben im Anamnesefragebogen habe ich wahrheitsgemäss beantwortet
                            </p>

                            <p>
                                Wir bitten Sie, uns umgehend über jegliche Änderungen Ihres Gesundheitszustandes zu informieren.
                                Dies umfasst, ist aber nicht beschränkt auf, neu diagnostizierte Erkrankungen, Änderungen bestehender
                                Krankheiten, die Einnahme neuer Medikamente oder Änderungen in der Medikation. Diese
                                Informationen sind entscheidend, um Ihre Behandlung optimal anzupassen. Bitte beachten Sie, dass wir
                                keinerlei Verantwortung für gesundheitliche Folgen übernehmen können, die auf nicht mitgeteilte
                                Änderungen zurückzuführen sind.
                            </p>

                            <p>
                                Vielen Dank für Ihr Verständnis und Ihre Kooperation.
                            </p>
                        </div>
                    </li>
                </ul>

                <div class="mb-9">
                    <ConsentSignature
                        ref="consentSignature"
                        :customer="customer"
                        :form="form"
                        :v$="v$"
                        :is-edit="isEdit"
                    />
                </div>

                <div class="flex justify-end">
                    <Button
                        @click="submit"
                        :loading="submitting"
                        label="speichern"
                        class="min-w-[100px] !bg-black !text-[#FFF053] !border-none"
                    />
                </div>
            </div>
        </div>
    </Layout>
</template>
